import React, { useEffect, useState } from "react";
import { Button, Card, Icon, Label } from "semantic-ui-react";
import { getOrders } from "../../services/requests.service";
import Order from "../../types/entities/Order";
import LuxonDataTable from "../../components/LuxonDataTable";
import { daysPassedSince, formatDateTime } from "../../utils/datetimeUtils";
import InputSearch from "../../components/InputSearch";
import PageHeader from "../../components/PageHeader";
import StatusFilters from "../../components/StatusFilters";
import StatusFiltersOption from "../../types/models/StatusFiltersOption";
import RootTemplate from "../../components/RootTemplate";
import { getCurrentOrderStatusLabel } from "../../utils/helpers";

const OrdersList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [search, setSearch] = useState("");
    const [statusFilters, setStatusFilters] = useState<string[]>([]);

    const columns = [
        {
            name: "Folio",
            button: true,
            selector: (row: Order) => <Label horizontal>{`#${row.invoice}`}</Label>,
        },
        {
            name: "Fecha de compra",
            selector: (row: Order) => formatDateTime(row.createdAt),
        },
        {
            name: "Cliente",
            selector: (row: Order) => row.client?.person?.fullName ?? "",
        },
        {
            name: "Producto",
            selector: (row: Order) => row.product.name,
        },
        {
            name: "Dias transcurridos",
            selector: (row: Order) => `${daysPassedSince(row.createdAt)} dias`,
        },
        {
            name: "Estatus",
            selector: (row: Order) => getCurrentOrderStatusLabel(row.statusHistory)
        },
        {
            name: "Acciones",
            button: true,
            cell: (row: Order) => (
                <>
                    <Button secondary icon size="mini" href={`orders/${row.id}/details`}>
                        <Icon name="search" />
                    </Button>
                </>
            ),
        },
    ];

    const fetchData = async (page: number, search?: string, status?: string[]) => {
        setLoading(true);
        try {
            const response = await getOrders(perPage, page - 1, search, status, "createdAt,desc");
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
        } catch (error) {
            console.error("Error al obtener la informacion:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        fetchData(page, search, statusFilters);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        try {
            const response = await getOrders(newPerPage, page - 1, "", statusFilters, "createdAt,desc");
            setData(response?.data.content);
            setTotalRows(response?.data.totalElements);
            setPerPage(newPerPage);
        } catch (error) {
            console.error("Error al obtener la informacion:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        fetchData(1, search, statusFilters);
        // eslint-disable-next-line
    }, [search, statusFilters]);

    const statusList: StatusFiltersOption[] = [
        {
            name: "Validando pedido",
            statusKey: "IN_VALIDATION",
            color: "yellow",
        },
        {
            name: "En produccion",
            statusKey: "IN_PRODUCTION",
            color: "grey",
        },
        {
            name: "Recibido en almacen",
            statusKey: "RECEIVED_IN_WAREHOUSE",
            color: "olive",
        },
        {
            name: "Listo para agendar",
            statusKey: "READY_TO_SCHEDULE",
            color: "green",
        },
        {
            name: "Agendado",
            statusKey: "SCHEDULED",
            color: "orange",
        },
        {
            name: "Proyecto terminado",
            statusKey: "COMPLETED",
            color: "blue",
        },
        {
            name: "Proyecto inconcluso",
            statusKey: "INCOMPLETE",
            color: "brown",
        },
        {
            name: "Cancelado",
            statusKey: "CANCELED",
            color: "red",
        },
    ]

    return (
        <RootTemplate>

            <PageHeader title="Ordenes" />

            <InputSearch
                icon="search"
                placeholder="Búsqueda por nombre de cliente..."
                setSearch={setSearch}
            />

            <StatusFilters options={statusList} setStatusFilters={setStatusFilters} />

            <Card fluid>
                <LuxonDataTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    totalRows={totalRows}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                />
            </Card>
        </RootTemplate>
    );
};

export default OrdersList;
